<template>

  <rate-calendar-popup :title="title">

    <template v-slot:tools>

      <v-btn-group>
        <v-btn icon="$edit" :title="$t('actions.edit_default_rates')" @click="$emit('edit')" />
        <v-btn v-if="verticalSupportsSpotRates" icon="$spotRate" :title="$t('actions.add_spot_rate')" @click="$emit('addException')" />
      </v-btn-group>

    </template>

    <table class="calendar-popup-table">
      <thead>
        <tr>
          <th scope="col">{{ $t('titles.default_cost') }}</th>
          <th v-if="hasChannelOverrides" scope="col">{{ $t('titles.channel') }}</th>
          <th v-if="hasChannelOverrides" scope="col">{{ $t('titles.channel_override') }}</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="defaultRate in sortedDefaultRates">
          <tr v-for="(_, i) in Math.max(1, defaultRate.channelDefaultRates.length)">
            <td v-if="i === 0" :rowspan="Math.max(1, defaultRate.channelDefaultRates.length)">
              {{ currencyFormatter.format(defaultRate.defaultCost) }}
              <template v-if="tieredRates">
                for
                {{ defaultRate.perQuantity }}
                {{ defaultRate.perUnit }}{{ defaultRate.perQuantity !== null && defaultRate.perQuantity > 1 ? 's' : '' }}
              </template>
            </td>
            <td v-if="hasChannelOverrides">
              <template v-if="defaultRate.channelDefaultRates.length >= i+1">
                {{ ChannelRegistry.findTitle(defaultRate.channelDefaultRates[i].channel) }}
              </template>
            </td>
            <td v-if="hasChannelOverrides" class="text-right">
              <template v-if="defaultRate.channelDefaultRates.length >= i+1">
                <template v-if="defaultRate.channelDefaultRates[i].channelCost === defaultRate.defaultCost">
                  —
                </template>
                <template v-else>
                  {{ currencyFormatter.format(defaultRate.channelDefaultRates[i].channelCost) }}
                </template>
              </template>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

  </rate-calendar-popup>

</template>

<script setup lang="ts">

import { computed } from 'vue'

// components
import RateCalendarPopup from './RateCalendarPopup.vue'

// models
import { DefaultRate } from '../../../../../models/DefaultRate'

// misc
import ChannelRegistry from '../../../../../channels/ChannelRegistry'
import VerticalRegistry from '../../../../../verticals/VerticalRegistry'
import { currencyFormatter } from '../../../../../utils/formatters'
import type { NonEventBaseItem } from '../types'

interface Props {
  item: NonEventBaseItem
}

const props = defineProps<Props>()

const $emit = defineEmits(['edit', 'addException'])

const title = computed<string>(() => {
  return props.item.locationProduct.product.name
})

const sortedDefaultRates = computed<DefaultRate[]>(() => {
  return props.item.defaultRates.sort((dr1: DefaultRate, dr2: DefaultRate) => {
    const duration1 = dr1.getDurationInHours()
    const duration2 = dr2.getDurationInHours()
    if (duration1 === null || duration2 === null) {
      return 0
    }
    return duration1 - duration2
  })
})

const hasChannelOverrides = computed<boolean>(() => {
  return props.item.defaultRates.some(dr => {
    return dr.channelDefaultRates.some(cdr => cdr.channelCost !== dr.defaultCost)
  })
})

const tieredRates = computed<boolean>(() => {
  return VerticalRegistry.get(props.item.locationProduct?.product.verticalType)?.supports(['tieredRates']) ?? false
})

const verticalSupportsSpotRates = computed<boolean>(() => {
  return VerticalRegistry.get(props.item.locationProduct.product?.verticalType)?.supports(['spotRates']) ?? false
})

</script>
